import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Badge, Modal } from 'react-bootstrap';
import { FiLayers, FiRefreshCw, FiEye, FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';
import { fetchActivitySubcoveragesRequest, updateActivitySubcoverageRequest, deleteActivitySubcoverageRequest } from '../../store/actions/dataActions';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import './AdminActivitySubcoverages.css';

const EMPTY_ACTIVITY_SUBCOVERAGES_FILTERS = {
    company_name: '',
    reference_name: '',
    category: '',
};

const AdminActivitySubcoverages = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { activitySubcoverages, updateActivitySubcoverage, deleteActivitySubcoverage } = useSelector((state) => state.data);
    const { isAuthenticated } = useSelector((state) => state.adminAuth);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('activity-subcoverages', EMPTY_ACTIVITY_SUBCOVERAGES_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSubcoverageId, setSelectedSubcoverageId] = useState(null);
    const [selectedSubcoverageForDelete, setSelectedSubcoverageForDelete] = useState(null);
    const [formData, setFormData] = useState({
        sub_coverage_start_date: '',
        sub_coverage_end_date: '',
        trigger_sup: '',
        trigger_number_hours: '',
        unit: '',
        prcp_trigger_hour_inf: '',
        timing_hour: '',
        observation_timing_hour: '',
        trigger_hour_sup: '',
        timing_day: '',
        observation_timing_day: '',
        siv_ratio_maintained: '',
        siv_ratio_cancelled: '',
        city: '',
        zip_code: '',
        activity_address: '',
        country: '',
        trigger_type: '',
        coverage_code: '',
        orakle_price_ratio_excl_tsca: '',
        data_source: '',
        trigger_low: '',
        geo_longitude: '',
        geo_latitude: '',
    });

    const numberFields = ['trigger_number_hours', 'timing_hour', 'observation_timing_hour', 'timing_day', 'observation_timing_day'];

    const convertToDateInput = (value) => {
        if (!value || typeof value !== 'string') return '';
        const [day, month, year] = value.split('/');
        if (!day || !month || !year) return value;
        return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    const convertToApiDate = (value) => {
        if (!value || typeof value !== 'string') return value;
        const [year, month, day] = value.split('-');
        if (!year || !month || !day) return value;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.padStart(4, '0')}`;
    };

    const loadSubcoverages = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;
        Object.keys(activeFilters).forEach((key) => {
            const value = activeFilters[key];
            if (value && value.trim() !== '') {
                params[key] = value.trim();
            }
        });

        dispatch(fetchActivitySubcoveragesRequest(params));
    }, [dispatch, itemsPerPage, appliedFilters]);

    useEffect(() => {
        if (isAuthenticated) {
            loadSubcoverages(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    useEffect(() => {
        loadSubcoverages(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        if (updateActivitySubcoverage.success) {
            setShowEditModal(false);
            loadSubcoverages(currentPage);
        }
    }, [updateActivitySubcoverage.success, currentPage, loadSubcoverages]);

    useEffect(() => {
        if (deleteActivitySubcoverage.success) {
            setShowDeleteModal(false);
            setSelectedSubcoverageForDelete(null);
            loadSubcoverages(currentPage);
        }
    }, [deleteActivitySubcoverage.success, currentPage, loadSubcoverages]);


    const subcoveragesData = useMemo(() => {
        const payload = activitySubcoverages?.data;
        if (!payload) return [];
        const possibleArrays = [payload.subcoverages, payload.data, payload.records];
        const found = possibleArrays.find((entry) => Array.isArray(entry));
        if (Array.isArray(found)) return found;
        if (Array.isArray(payload)) return payload;
        return [];
    }, [activitySubcoverages?.data]);

    const meta = useMemo(() => activitySubcoverages?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    }, [activitySubcoverages?.data, itemsPerPage, currentPage]);

    const totalPages = meta.total_pages || Math.max(1, Math.ceil((meta.total || 0) / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + subcoveragesData.length, totalItems);

    const handleRefresh = () => {
        loadSubcoverages(currentPage);
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadSubcoverages(1, filters);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const empty = { ...EMPTY_ACTIVITY_SUBCOVERAGES_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadSubcoverages(1, empty);
        }, 100);
    };

    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = startPage + maxVisiblePages - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let page = startPage; page <= endPage; page += 1) {
            pages.push(page);
        }
        return pages;
    };

    const renderValue = (value, fallback = 'N/A') => {
        if (value === undefined || value === null || value === '') {
            return fallback;
        }
        return value;
    };

    const openEditModal = (item) => {
        const subcoverage = item?.subcoverage || {};
        setSelectedSubcoverageId(subcoverage.sub_coverage_id || null);
        setFormData({
            sub_coverage_start_date: convertToDateInput(subcoverage.sub_coverage_start_date) || '',
            sub_coverage_end_date: convertToDateInput(subcoverage.sub_coverage_end_date) || '',
            trigger_sup: subcoverage.trigger_sup || '',
            trigger_number_hours: subcoverage.trigger_number_hours ?? '',
            unit: subcoverage.unit || '',
            prcp_trigger_hour_inf: subcoverage.prcp_trigger_hour_inf || '',
            timing_hour: subcoverage.timing_hour ?? '',
            observation_timing_hour: subcoverage.observation_timing_hour ?? '',
            trigger_hour_sup: subcoverage.trigger_hour_sup || '',
            timing_day: subcoverage.timing_day ?? '',
            observation_timing_day: subcoverage.observation_timing_day ?? '',
            siv_ratio_maintained: subcoverage.siv_ratio_maintained || '',
            siv_ratio_cancelled: subcoverage.siv_ratio_cancelled || '',
            city: subcoverage.city || '',
            zip_code: subcoverage.zip_code || '',
            activity_address: subcoverage.activity_address || '',
            country: subcoverage.country || '',
            trigger_type: subcoverage.trigger_type || '',
            coverage_code: subcoverage.coverage_code || '',
            orakle_price_ratio_excl_tsca: subcoverage.orakle_price_ratio_excl_tsca || '',
            data_source: subcoverage.data_source || '',
            trigger_low: subcoverage.trigger_low || '',
            geo_longitude: subcoverage.geo_longitude || '',
            geo_latitude: subcoverage.geo_latitude || '',
        });
        setShowEditModal(true);
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditSubmit = (event) => {
        event.preventDefault();
        if (!selectedSubcoverageId) return;

        const payload = Object.keys(formData).reduce((acc, key) => {
            const rawValue = formData[key];
            if (rawValue === '') {
                acc[key] = null;
                return acc;
            }
            if (numberFields.includes(key)) {
                const parsed = Number(rawValue);
                acc[key] = Number.isNaN(parsed) ? null : parsed;
            } else if (key === 'sub_coverage_start_date' || key === 'sub_coverage_end_date') {
                acc[key] = convertToApiDate(rawValue);
            } else {
                acc[key] = rawValue;
            }
            return acc;
        }, {});

        dispatch(updateActivitySubcoverageRequest(selectedSubcoverageId, payload));
    };

    const openDeleteModal = (item) => {
        const subcoverage = item?.subcoverage || {};
        setSelectedSubcoverageForDelete(item);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedSubcoverageForDelete(null);
    };

    const handleConfirmDelete = () => {
        if (selectedSubcoverageForDelete) {
            const subcoverage = selectedSubcoverageForDelete?.subcoverage || {};
            const subcoverageId = subcoverage.sub_coverage_id;
            if (subcoverageId) {
                dispatch(deleteActivitySubcoverageRequest(subcoverageId));
            }
        }
    };

    return (
        <div className="dashboard-page admin-activity-subcoverages-page">
            <Card className="mb-3">
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiLayers className="me-2" />
                                Activity - Gestion Sous-couvertures
                            </h2>
                            <p className="text-muted mb-0">Liste des sous-couvertures associées aux activités avec filtres avancés.</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" onClick={handleRefresh} disabled={activitySubcoverages.loading}>
                                <FiRefreshCw className={activitySubcoverages.loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                            <Button variant="primary" onClick={() => navigate('/activity/subcoverages/create')}>
                                <FiPlus /> Ajouter une sous-couverture
                            </Button>
                        </div>
                    </div>

                    {activitySubcoverages.error && (
                        <Alert variant="danger" className="mt-3">
                            {activitySubcoverages.error}
                        </Alert>
                    )}
                    {updateActivitySubcoverage.success && (
                        <Alert variant="success" className="mt-3">
                            Sous-couverture mise à jour avec succès.
                        </Alert>
                    )}
                    {updateActivitySubcoverage.error && (
                        <Alert variant="danger" className="mt-3">
                            {updateActivitySubcoverage.error}
                        </Alert>
                    )}
                    {deleteActivitySubcoverage.success && (
                        <Alert variant="success" className="mt-3">
                            Sous-couverture supprimée avec succès.
                        </Alert>
                    )}
                    {deleteActivitySubcoverage.error && (
                        <Alert variant="danger" className="mt-3">
                            {deleteActivitySubcoverage.error}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Partenaire (company name)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: Mahe Blanchard"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Référence</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reference_name"
                                            value={filters.reference_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom de la référence"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Catégorie</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="category"
                                            value={filters.category}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Catégorie"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span>
                                Résultats : {indexOfFirstItem + 1} - {indexOfLastItem} sur {totalItems}
                            </span>
                            {activitySubcoverages.loading && <Spinner animation="border" size="sm" />}
                        </Card.Header>
                        <div className="table-responsive table-scroll">
                            <Table hover striped className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Code couverture</th>
                                        <th>Référence</th>
                                        <th>Catégorie</th>
                                        <th>Partenaire</th>
                                        <th>Ville</th>
                                        <th>Pays</th>
                                        <th>Type</th>
                                        <th>Période</th>
                                        <th className="col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activitySubcoverages.loading ? (
                                        <tr>
                                            <td colSpan={9} className="text-center py-4">
                                                <Spinner animation="border" role="status" />
                                            </td>
                                        </tr>
                                    ) : subcoveragesData.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center py-4">
                                                Aucune sous-couverture trouvée.
                                            </td>
                                        </tr>
                                    ) : (
                                        subcoveragesData.map((item, index) => {
                                            const subcoverage = item?.subcoverage || {};
                                            const reference = item?.reference || {};
                                            const partner = item?.partner_info || {};

                                            return (
                                                <tr key={subcoverage.sub_coverage_id || index}>
                                                    <td>
                                                        <div className="fw-semibold">{renderValue(subcoverage.coverage_code)}</div>
                                                        <div className="text-muted small">{renderValue(subcoverage.sub_coverage_id)}</div>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{renderValue(reference.name)}</div>
                                                        <div className="text-muted small">{renderValue(reference.reference_code)}</div>
                                                    </td>
                                                    <td>{renderValue(reference.category)}</td>
                                                    <td>
                                                        <div className="fw-semibold">{renderValue(partner.company_name, `${renderValue(partner.first_name, '')} ${renderValue(partner.last_name, '')}`)}</div>
                                                        <div className="text-muted small">{renderValue(partner.user_code)}</div>
                                                    </td>
                                                    <td>{renderValue(subcoverage.city)}</td>
                                                    <td>{renderValue(subcoverage.country)}</td>
                                                    <td>
                                                        <Badge bg="info" text="dark" className="text-capitalize">
                                                            {renderValue(subcoverage.trigger_type)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div>{renderValue(subcoverage.sub_coverage_start_date)}</div>
                                                        <div className="text-muted small">{renderValue(subcoverage.sub_coverage_end_date)}</div>
                                                    </td>
                                                    <td className="col-actions">
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (subcoverage.sub_coverage_id) {
                                                                        navigate(`/activity/subcoverages/${subcoverage.sub_coverage_id}`);
                                                                    }
                                                                }}
                                                            >
                                                                <FiEye className="me-1" /> Détails
                                                            </Button>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => openEditModal(item)}
                                                            >
                                                                <FiEdit className="me-1" /> Modifier
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => openDeleteModal(item)}
                                                            >
                                                                <FiTrash2 className="me-1" /> Supprimer
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                            Page {currentPage} sur {totalPages}
                        </div>
                        <Pagination className="mb-0">
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                            {getPaginationRange().map((page) => (
                                <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                                    {page}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Modifier la sous-couverture</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form id="activity-subcoverage-edit-form" onSubmit={handleEditSubmit}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Date de début</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="sub_coverage_start_date"
                                        value={formData.sub_coverage_start_date}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Date de fin</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="sub_coverage_end_date"
                                        value={formData.sub_coverage_end_date}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Trigger low</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="trigger_low"
                                        value={formData.trigger_low}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Trigger number hours</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="trigger_number_hours"
                                        value={formData.trigger_number_hours}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Trigger hour inf</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="prcp_trigger_hour_inf"
                                        value={formData.prcp_trigger_hour_inf}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Unité</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="unit"
                                        value={formData.unit}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Timing hour</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="timing_hour"
                                        value={formData.timing_hour}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Observation hour</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="observation_timing_hour"
                                        value={formData.observation_timing_hour}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Timing day</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="timing_day"
                                        value={formData.timing_day}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Observation day</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="observation_timing_day"
                                        value={formData.observation_timing_day}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>SIV Maintien</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="siv_ratio_maintained"
                                        value={formData.siv_ratio_maintained}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>SIV Annulation</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="siv_ratio_cancelled"
                                        value={formData.siv_ratio_cancelled}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Type de déclencheur</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="trigger_type"
                                        value={formData.trigger_type}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Adresse activité</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="activity_address"
                                        value={formData.activity_address}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Ville</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Code postal</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="zip_code"
                                        value={formData.zip_code}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Pays</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Coverage code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="coverage_code"
                                        value={formData.coverage_code}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Data source</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="data_source"
                                        value={formData.data_source}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Orakle ratio excl. TSCA</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="orakle_price_ratio_excl_tsca"
                                        value={formData.orakle_price_ratio_excl_tsca}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Longitude</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="geo_longitude"
                                        value={formData.geo_longitude}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Latitude</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="geo_latitude"
                                        value={formData.geo_latitude}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="activity-subcoverage-edit-form"
                        disabled={updateActivitySubcoverage.loading}
                    >
                        {updateActivitySubcoverage.loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteActivitySubcoverage.error && (
                        <Alert variant="danger" className="mb-3">
                            {deleteActivitySubcoverage.error}
                        </Alert>
                    )}
                    <p>
                        Êtes-vous sûr de vouloir supprimer la sous-couverture{' '}
                        <strong>
                            {selectedSubcoverageForDelete?.subcoverage?.coverage_code || 
                             selectedSubcoverageForDelete?.subcoverage?.sub_coverage_id || 
                             'cette sous-couverture'}
                        </strong> ?
                    </p>
                    <p className="text-muted mb-0">
                        Cette action est irréversible.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteActivitySubcoverage.loading}>
                        Annuler
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={deleteActivitySubcoverage.loading}
                    >
                        {deleteActivitySubcoverage.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            <>
                                <FiTrash2 className="me-2" />
                                Supprimer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminActivitySubcoverages;

